import type { TPaymentMethods } from "@features/checkout/types";
import { parseAdyenTranslations } from "./parseAdyenTranslations";
import { AdyenCheckout, Dropin } from "@adyen/adyen-web";
import type {
    ResultCode,
    CoreConfiguration,
    DropinConfiguration,
    PaymentAmount,
    CheckoutAdvancedFlowResponse,
    UIElement,
    UIElementProps,
    SubmitActions,
    PaymentAction,
} from "@adyen/adyen-web";
import { getBaseConfiguration } from "@features/checkout/components/payment-step/utils/adyen/getBaseConfiguration";
import { activeSiteCountry } from "@config/site/site-config";
import { createPaymentLogger } from "./createPaymentLogger";
import { processPayment } from "../../actions/processPayment";
import { getSessionIDForKount } from "@lib/utils/kount/session";
import { parsePaymentAction } from "@features/checkout/components/payment-step/utils/adyen/parsePaymentAction";
import type { AdyenRefusalResponse } from "./parsePaymentResult";
import { parsePaymentResult } from "./parsePaymentResult";
import { isFailedAdyenResult } from "./isFailedAdyenResult";
import type { CheckoutErrorType, HeapPaymentErrorType } from "@lib/utils/checkout-utility/types";
import { HEAP_PAYMENT_ERRORS } from "@lib/utils/checkout-utility/types";
import { confirmPayment } from "../../actions/confirmPayment";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CheckoutProgressSubtypes } from "@lib/analytics/analyticsTypes";
import type { UtmModel } from "@interfaces/heap-api";
import type { Cart } from "@graphql/generated/types";
import { EcontextPaymentMethod } from "@lib/constants/payment";
import { toAdyenPaymentMethods } from "@lib/utils/payment-methods/toAdyenPaymentMethods";
import { trackEvent } from "@components/ab-test/wingify/event";
import type { CartCheckoutValidationResult } from "@features/checkout/components/payment-step/hooks/useValidateBasket";
import {
    markRedirectAttempt,
    clearRedirectAttemptMarker,
} from "@lib/monitoring/navigationAbortError";
import type { PlacedOrder } from "./pendingPaymentStorage";
import {
    isUpiPaymentMethod,
    PAYMENT_COMPLETED_STORAGE_KEY,
    PENDING_QR_ACTION_STORAGE_KEY,
} from "./pendingPaymentStorage";

// Survives a dropin remount, which qrCode flows (Swish) hit when the mobile app-switch
// reloads the page and no redirectResult/paymentFlowId is present in the URL.
const PAYMENT_FLOW_ID_STORAGE_KEY = "adyen_payment_flow_id";

type PendingQrAction = {
    cartId: string;
    paymentFlowId?: string;
    action: PaymentAction;
    // UPI only: the order HEAP placed before the QR was paid.
    placedOrder?: PlacedOrder;
};

const readPendingQrAction = (): PendingQrAction | undefined => {
    try {
        const stored = sessionStorage.getItem(PENDING_QR_ACTION_STORAGE_KEY);
        const parsed = stored ? (JSON.parse(stored) as PendingQrAction) : undefined;
        return parsed?.action?.type && parsed.cartId ? parsed : undefined;
    } catch {
        return undefined;
    }
};
// Module scope, so the instance a remount creates shares it — both live in the same document.
// Check-and-set here is atomic because JS is single-threaded, and a page load starts empty, so
// there is no lease that can go stale and block a legitimate confirm. Keyed per cart and flow.
const confirmsInFlight = new Map<string, ReturnType<typeof confirmPayment>>();
// Diagnostics for the Swish duplicate-poller investigation. documentId is fixed per page load, so
// two instances sharing it were built in the same document; differing ids mean the app-switch
// return created a new one, which is what decides whether client-side coordination can work here.
const documentId = Math.random().toString(36).slice(2, 10);
let instanceCounter = 0;

export type CreateAdyenInstanceParams = {
    apiKey: string;
    amount: PaymentAmount;
    cartId: string;
    paymentMethods: TPaymentMethods[];
    // JSON stringified translations for the Adyen component. It's maintained directly in the CMS so we just pass it through.
    translations?: string;
    locale: string;
    // Additional params passed to logs.
    logParams: Record<string, unknown>;
    // Where to mount the Adyen Dropin component.
    targetElement: HTMLElement;
    // Redirect result if we're returning to the checkout after handling payment or 3DS on a different website.
    redirectResult?: string;
    // paymentFlowId read from the return URL, identifying which attempt's draft this redirect confirms.
    redirectPaymentFlowId?: string;
    utm?: UtmModel;
    isSubscribed?: boolean;
    shopperConversionId?: string;
    validateBasket?: (
        paymentMethod?: string,
        inputCart?: Cart,
        isPaymentContinuation?: boolean
    ) => Promise<CartCheckoutValidationResult>;
    getCurrentCart?: () => Promise<Cart | undefined>;
    getCart: () => Cart | undefined;
    // Handle successful payment. orderDraftId is passed when available so the
    // Confirmation Page can query the exact draft rather than by orderNumber
    // (multiple attempts, when failing, can share the same orderNumber).
    onSuccess: (orderNumber: string, orderDraftId?: string | null) => void;
    onError: (
        error: HeapPaymentErrorType | AdyenRefusalResponse | CheckoutErrorType,
        errValue?: string
    ) => void;
    // Trigers when the payment starts.
    onPaymentStart: () => void;
    // Whether a qrCode (Swish) action is on screen. The QR replaces the payment methods with no way
    // back, so the consumer renders a cancel control while this is true.
    onPendingQrChange?: (active: boolean) => void;
    // Ability to release the lockscreen in case of verification or cancelled flows
    releaseLockscreen: () => void;
    // Shopper backed out of a redirect/3DS/off-site popup flow that we ourselves initiated. No
    // error to show, but the consumer should bring the payment step back into view for the shopper.
    onCancel?: () => void;
};

type AwaitPaymentState = { active: false } | ({ active: true } & PlacedOrder);

export type AdyenInstanceControls = {
    updateAmount: (value: PaymentAmount) => void;
    mountDropin: () => void;
    updateCartId: (newCartId: string) => void;
    updateValidateBasket: (newValidateBasket: CreateAdyenInstanceParams["validateBasket"]) => void;
    // Abandons a displayed qrCode and brings the payment methods back. Client-side only: it cannot
    // cancel the request at Adyen, so a shopper who already paid in the app still completes.
    cancelPendingQr: () => void;
};
export const createAdyenInstance = async ({
    apiKey,
    amount,
    cartId: originalCartId,
    paymentMethods,
    translations,
    locale,
    logParams,
    targetElement,
    redirectResult,
    redirectPaymentFlowId,
    utm,
    isSubscribed,
    shopperConversionId,
    validateBasket: originalValidateBasket,
    getCurrentCart,
    getCart,
    onSuccess,
    onError,
    onPaymentStart,
    onPendingQrChange,
    releaseLockscreen,
    onCancel,
}: CreateAdyenInstanceParams): Promise<AdyenInstanceControls> => {
    let hasFiredDefaultOnSelect = false;
    let cartId = originalCartId;
    let validateBasket = originalValidateBasket;
    let awaitPaymentState: AwaitPaymentState = { active: false };
    // UPI QR only: the order HEAP placed with the QR, opened if the QR ends unpaid.
    let pendingUpiOrder: PlacedOrder | undefined;
    // Set when an attempt starts here (onSubmit). Empty after a redirect return (fresh instance),
    // where we fall back to redirectPaymentFlowId from the URL.
    let paymentFlowId: string | undefined;
    // Latched on success only, so a genuine failure still lets the shopper retry. Guards against a
    // duplicate onAdditionalDetails resolving after the order is already placed.
    let paymentCompleted = false;
    // What is currently on screen, kept so cancelPendingQr can tear it down. In the normal flow the
    // QR lives inside the dropin (componentFromAction); after a reload it is mounted standalone.
    let pendingQrElement: UIElement | undefined;
    let dropinElement: InstanceType<typeof Dropin> | undefined;
    // Concurrent confirms are deduped through the module-level confirmsInFlight map above, so a
    // remounted instance joins the pending call instead of sending its own.

    // Undefined rather than "": an empty id can never match a key in the order-draft map, so it
    // turns a recoverable state into a confirmPayment throw the shopper sees as a payment error.
    const resolvePaymentFlowId = () =>
        paymentFlowId ??
        redirectPaymentFlowId ??
        sessionStorage.getItem(PAYMENT_FLOW_ID_STORAGE_KEY) ??
        undefined;

    const isPaymentAlreadyCompleted = () =>
        paymentCompleted ||
        sessionStorage.getItem(PAYMENT_COMPLETED_STORAGE_KEY) === originalCartId;

    const {
        info: logInfo,
        warn: logWarn,
        error: logError,
    } = createPaymentLogger({
        prefix: "CreateAdyenInstance:",
        data: {
            cartId,
            // Stamped on every line so a trace shows which instance did what.
            instanceId: `${documentId}-${++instanceCounter}`,
            ...logParams,
        },
    });
    const parsedTranslation = parseAdyenTranslations(translations);

    // The single path for handling an Adyen action, used by both onSubmit and onAdditionalDetails.
    // This SDK version settles the callback's promise only through actions.resolve()/reject(), and
    // once resolved with an `action`, dispatches it itself via elementRef.handleAction() (the
    // `component` argument each callback receives) — see UIElement.handleResponse in
    // @adyen/adyen-web. So for a mounted component, resolving is the whole job: it mounts the
    // action and settles the promise in one step. Calling handleAction() ourselves as well would
    // mount it twice on the same component.
    // checkoutInstance.submitDetails() (see handleRedirectResult) calls onAdditionalDetails with no
    // component, and the core's own submitDetails() only auto-mounts a resolved action when an
    // afterAdditionalDetails callback is configured, which we don't do — so resolving alone would
    // drop the action there. Mount it ourselves via the core in that case, same as
    // mountPendingQrAction does after a reload.
    const dispatchAction = (
        component: UIElement<UIElementProps> | undefined,
        action: PaymentAction,
        resultCode: ResultCode | undefined,
        actions: SubmitActions | undefined,
        label: string
    ) => {
        const isRedirect = action?.type === "redirect";
        if (isRedirect) {
            markRedirectAttempt();
        }
        if (!component) {
            try {
                checkoutInstance.createFromAction(action).mount(targetElement);
            } catch (error) {
                // Nothing was actually mounted (no navigation, no inline UI), so a marker set above
                // would make an unrelated later fetch failure look like a benign navigation abort.
                if (isRedirect) {
                    clearRedirectAttemptMarker();
                }
                logError(`${label}: createFromAction/mount failed`, {
                    actionType: action?.type,
                    paymentFlowId: resolvePaymentFlowId(),
                    error,
                });
                actions?.reject?.(error);
                throw error;
            }
        }
        actions?.resolve?.({ action, resultCode } as CheckoutAdvancedFlowResponse);
    };

    const baseConfig = await getBaseConfiguration({
        paymentMethods: paymentMethods,
    });

    const dropinConfiguration: DropinConfiguration = {
        ...baseConfig,
        onReady: () => {
            logInfo("onReady event");
        },

        onSelect: (paymentMethod) => {
            AnalyticsClient.checkoutProgress(
                CheckoutProgressSubtypes.paymentMethodSelected,
                paymentMethod.type,
                hasFiredDefaultOnSelect ? "user" : "default"
            );
            hasFiredDefaultOnSelect = true;

            logInfo("onSelect event", {
                paymentMethod,
            });
        },
    };

    const handleSuccessfulPayment = async (
        orderNumber: string,
        orderDraftId?: string | null,
        paymentMethod?: string,
        // True when another poller already placed this order. Navigation is idempotent, the purchase
        // event is not, and the pollers can sit in different tabs where no client state is shared.
        wasAlreadyConfirmed?: boolean
    ) => {
        // A duplicate poller can resolve after the order is already placed. Re-running would fire a
        // second purchase event and a second confirmation redirect.
        if (isPaymentAlreadyCompleted()) {
            logInfo("Payment already completed, ignoring duplicate success", { orderNumber });
            return;
        }
        paymentCompleted = true;
        sessionStorage.setItem(PAYMENT_COMPLETED_STORAGE_KEY, originalCartId);
        logInfo("Payment completed successfully");
        // Terminal outcome: clear the redirect-return marker so a later, unrelated
        // attempt can't accidentally inherit this attempt's payment method.
        sessionStorage.removeItem("adyen_payment_method_type");
        sessionStorage.removeItem(PAYMENT_FLOW_ID_STORAGE_KEY);
        sessionStorage.removeItem(PENDING_QR_ACTION_STORAGE_KEY);
        onPendingQrChange?.(false);
        const cart = getCart();
        if (cart && !wasAlreadyConfirmed) {
            // Analytics must not block or fail the success path: a rejection here
            // would skip onSuccess (no confirmation redirect) and, at the awaited
            // onSubmit callsite, bubble to the outer catch and trigger handleFailedPayment
            // even though the order is already authorised.
            try {
                await AnalyticsClient.purchaseAdded({ ...cart, orderNumber }, paymentMethod);

                trackEvent(`Adyen - ${paymentMethod ?? "unknown"}`, cart);
            } catch (error) {
                logError("Analytics purchaseAdded failed", { error });
            }
        }
        onSuccess(orderNumber, orderDraftId);
    };

    // A UPI order exists from its QR on, so any end of that QR other than a verified payment opens
    // it; its confirmation page shows HEAP's final status.
    const openPendingUpiOrder = (reason: string): boolean => {
        if (!pendingUpiOrder) return false;
        const { orderNumber, orderDraftId, paymentMethod } = pendingUpiOrder;
        pendingUpiOrder = undefined;
        logInfo(`Opening placed UPI order: ${reason}`, { orderNumber });
        void handleSuccessfulPayment(orderNumber, orderDraftId, paymentMethod);
        return true;
    };

    const handleFailedPayment = ({
        message,
        component,
        actions,
        payload,
        severity = "error",
    }: {
        message: string;
        component: UIElement<UIElementProps>;
        actions: SubmitActions;
        payload?: Record<string, unknown>;
        severity?: "warn" | "error";
    }) => {
        const heapErrorCode = (payload?.result as { errorCode?: HeapPaymentErrorType })?.errorCode;
        const refusalResponse = payload?.adyenResult as AdyenRefusalResponse | undefined;
        const { errorCode: checkoutErrorCode, errorValue: checkoutErrorValue } =
            (payload?.basketValidation as
                | {
                      errorCode?: CheckoutErrorType;
                      errorValue?: string;
                  }
                | undefined) ?? {};
        const sanitizedPaymentDetails = {
            heapErrorCode,
            checkoutErrorCode,
        };

        // The order is already placed, so this is a losing duplicate. Surfacing it would replace the
        // confirmation page with a payment error. Checked across instances, not just this closure.
        if (isPaymentAlreadyCompleted()) {
            logWarn(
                `Payment already completed, ignoring late failure: ${message}`,
                sanitizedPaymentDetails
            );
            return;
        }
        const log = severity === "warn" ? logWarn : logError;
        const sanitizedPayload = payload
            ? {
                  ...payload,
                  ...(payload.basketValidation && typeof payload.basketValidation === "object"
                      ? {
                            basketValidation: Object.fromEntries(
                                Object.entries(payload.basketValidation).filter(
                                    ([key]) => key !== "errorValue"
                                )
                            ),
                        }
                      : {}),
              }
            : undefined;
        // Records why the completion latch did not suppress this failure: either no instance has
        // succeeded yet, or the stored cart does not match this one.
        log(message, {
            ...sanitizedPayload,
            completionLatch: {
                inMemory: paymentCompleted,
                stored: sessionStorage.getItem(PAYMENT_COMPLETED_STORAGE_KEY),
                cart: originalCartId,
                storedFlowId: sessionStorage.getItem(PAYMENT_FLOW_ID_STORAGE_KEY),
            },
        });
        if (openPendingUpiOrder(message)) return;
        // Terminal outcome: clear the redirect-return marker so a later, unrelated
        // attempt can't accidentally inherit this attempt's payment method.
        sessionStorage.removeItem("adyen_payment_method_type");
        // Terminal too: a failed attempt's QR must not be rebuilt on the next page load.
        sessionStorage.removeItem(PENDING_QR_ACTION_STORAGE_KEY);
        onPendingQrChange?.(false);
        // The flow id deliberately survives a failure: a concurrent instance created by the
        // app-switch remount has no other way to recover it, and onSubmit overwrites it per attempt.

        if (heapErrorCode) {
            onError(heapErrorCode);
        } else if (refusalResponse?.refusalReason) {
            onError(refusalResponse);
        } else if (checkoutErrorCode) {
            onError(checkoutErrorCode, checkoutErrorValue);
        } else {
            onError(HEAP_PAYMENT_ERRORS.PAYMENT_FAILED);
        }

        // Return the dropin UI to it's original state
        actions?.resolve?.({ resultCode: "Error" });
        component?.setStatus("ready");
    };

    const handleSubmit = async (
        state: Parameters<NonNullable<CoreConfiguration["onSubmit"]>>[0],
        component: Parameters<NonNullable<CoreConfiguration["onSubmit"]>>[1],
        actions: Parameters<NonNullable<CoreConfiguration["onSubmit"]>>[2]
    ) => {
        // Fire event when user clicks the payment button to go to payment gateway
        AnalyticsClient.checkoutProgress(CheckoutProgressSubtypes.goToPaymentGateway);
        try {
            onPaymentStart();
            logInfo("onSubmit start", {
                state,
            });
            // A new attempt on this cart must be able to surface its own failures, so drop both
            // completion latches left by an earlier one. The previous attempt is over either way.
            paymentCompleted = false;
            sessionStorage.removeItem(PAYMENT_COMPLETED_STORAGE_KEY);
            // This attempt supersedes any stored QR, so it must not be restored on a later load.
            sessionStorage.removeItem(PENDING_QR_ACTION_STORAGE_KEY);
            // Nor open an order an earlier attempt placed.
            pendingUpiOrder = undefined;
            const { paymentMethod, browserInfo } = state.data;
            const currentCart = await getCurrentCart?.();
            if (getCurrentCart && (!currentCart || currentCart.id !== cartId)) {
                logError("Current CommerceTools cart is unavailable or changed", {
                    currentCartId: currentCart?.id,
                    expectedCartId: cartId,
                });
                onError(HEAP_PAYMENT_ERRORS.CartValidationFailure);
                return;
            }

            const basketValidation = await validateBasket?.(
                paymentMethod.brand ?? paymentMethod.type,
                currentCart
            );
            // Blocks checkout when cart is empty / OOS / invalid / contact invalid —
            // keep as error for Datadog with structured reason fields when present.
            if (basketValidation?.isValid === false) {
                logError("Basket validation failed during onSubmit", {
                    reason: basketValidation.reason,
                    cartId: basketValidation.cartId,
                    skus: basketValidation.skus,
                    errorCode: basketValidation.errorCode,
                });
                handleFailedPayment({
                    message: "Cart checkout validation failed during onSubmit",
                    component,
                    actions,
                    payload: {
                        basketValidation,
                        state,
                    },
                });
                return;
            }

            const result = await processPayment({
                browserUserAgent: browserInfo?.userAgent ?? navigator.userAgent,
                providerType: "Adyen",
                paymentMethod: paymentMethod.type,
                cartId,
                locale,
                paymentRequestJson: JSON.stringify(state.data),
                utm,
                isSubscribed,
                shopperConversionId,
                kountSessionId: getSessionIDForKount(),
            });
            // Track this attempt's id so an in-page onAdditionalDetails (e.g. 3DS) confirms its draft.
            paymentFlowId = result.paymentFlowId;
            // Non-redirect actions (qrCode/Swish) never get paymentFlowId back via the return URL,
            // so persist it for the fresh instance created by a mobile app-switch remount.
            if (paymentFlowId) {
                sessionStorage.setItem(PAYMENT_FLOW_ID_STORAGE_KEY, paymentFlowId);
            }
            const resultCode = result.resultCode as ResultCode;
            logInfo("Payment result received from processPayment", {
                resultCode,
                message: result.message,
                ...(result?.isPaymentPending && { isPaymentPending: result.isPaymentPending }),
                ...(result?.isPaymentComplete && { isPaymentComplete: result.isPaymentComplete }),
            });

            // Unhappy path: processPayment failed to create an order or returned a non-authorised result.
            if (!result.success && !!result.errorCode) {
                handleFailedPayment({
                    message: "Error during payment",
                    component,
                    actions,
                    payload: {
                        result,
                        state,
                    },
                });
                return;
            }

            const adyenResult = parsePaymentResult(result.responseJson);

            // Failed payment result from Adyen.
            if (isFailedAdyenResult(adyenResult.resultCode)) {
                handleFailedPayment({
                    message:
                        (adyenResult as AdyenRefusalResponse).refusalReason ??
                        "Payment failed with Adyen result",
                    component,
                    actions,
                    payload: {
                        adyenResult,
                        onSubmitData: state.data,
                    },
                    severity: "warn",
                });
                return;
            }

            // HEAP places a UPI order with its QR, so that pending answer still waits for the scan.
            const upiPlacedOrder: PlacedOrder | undefined =
                isUpiPaymentMethod(paymentMethod.type) &&
                result.isPaymentPending &&
                result.orderNumber &&
                parsePaymentAction(result.responseJson)?.action.type === "qrCode"
                    ? {
                          orderNumber: result.orderNumber,
                          orderDraftId: result.orderDraftId,
                          paymentMethod: paymentMethod.brand ?? paymentMethod.type,
                      }
                    : undefined;

            // Happy path payment completed. SevenEleven uses redirect.
            if (
                (result?.isPaymentComplete || (result?.isPaymentPending && !upiPlacedOrder)) &&
                result?.orderNumber &&
                paymentMethod.type !== EcontextPaymentMethod.SevenEleven
            ) {
                logInfo("onSubmit: Payment completed successfully");
                actions.resolve?.(result as CheckoutAdvancedFlowResponse);
                await handleSuccessfulPayment(
                    result.orderNumber,
                    result.orderDraftId,
                    // Analytics wants the card brand (e.g. "visa", "mc") when available;
                    // non-card methods (paypal, klarna, ideal...) don't have one.
                    state.data.paymentMethod.brand ?? state.data.paymentMethod.type
                );
                return;
            }

            const resultAction = parsePaymentAction(result.responseJson);
            const actionType = resultAction?.action?.type;

            if (!actionType) {
                throw new Error(
                    "No action type found in Adyen result, cannot handle payment action",
                    {
                        cause: {
                            resultAction,
                            result,
                        },
                    }
                );
            }

            switch (actionType) {
                // Perform redirect to 3rd party. For example Klarna.
                case "redirect": {
                    // Track the redirect so the back-button/bfcache handler in HeapPaymentStepForm
                    // can refresh the frozen dropin when the user returns without completing payment.
                    sessionStorage.setItem(
                        "adyen_payment_method_type",
                        state.data.paymentMethod.brand ?? state.data.paymentMethod.type
                    );
                    dispatchAction(
                        component,
                        resultAction.action,
                        resultCode,
                        actions,
                        "onSubmit/redirect"
                    );
                    return;
                }
                // Handle inline 3DS2 verification.
                case "threeDS2": {
                    releaseLockscreen();
                    // Same marker as the "redirect" case: onAdditionalDetails resolves this flow
                    // via a fresh instance whose state.data doesn't carry the payment method.
                    sessionStorage.setItem(
                        "adyen_payment_method_type",
                        state.data.paymentMethod.brand ?? state.data.paymentMethod.type
                    );
                    dispatchAction(
                        component,
                        resultAction.action,
                        resultCode,
                        actions,
                        "onSubmit/threeDS2"
                    );
                    return;
                }
                // Native applications like Apple Pay or Google Pay or payment methods with popups like PayPal.
                case "sdk": {
                    // Same marker as the "redirect" case: onAdditionalDetails resolves this flow
                    // via a fresh instance whose state.data doesn't carry the payment method.
                    sessionStorage.setItem(
                        "adyen_payment_method_type",
                        state.data.paymentMethod.brand ?? state.data.paymentMethod.type
                    );
                    dispatchAction(
                        component,
                        resultAction.action,
                        resultCode,
                        actions,
                        "onSubmit/sdk"
                    );
                    return;
                }
                // QR code based payments like Swish. On desktop Adyen renders a QR code,
                // on mobile the SDK renders a button that opens the payment app via deep link.
                // The SDK handles status polling internally via the QRLoader component.
                case "qrCode": {
                    releaseLockscreen();
                    // Same marker as the "redirect" case: onAdditionalDetails resolves this flow
                    // via a fresh instance whose state.data doesn't carry the payment method.
                    sessionStorage.setItem(
                        "adyen_payment_method_type",
                        state.data.paymentMethod.brand ?? state.data.paymentMethod.type
                    );
                    pendingUpiOrder = upiPlacedOrder;
                    // The shopper stays on the page for a QR, so a reload (which the mobile
                    // app-switch return can trigger) would otherwise strand this attempt.
                    sessionStorage.setItem(
                        PENDING_QR_ACTION_STORAGE_KEY,
                        JSON.stringify({
                            cartId: originalCartId,
                            paymentFlowId,
                            action: resultAction.action,
                            placedOrder: upiPlacedOrder,
                        } satisfies PendingQrAction)
                    );
                    dispatchAction(
                        component,
                        resultAction.action,
                        resultCode,
                        actions,
                        "onSubmit/qrCode"
                    );
                    onPendingQrChange?.(true);
                    return;
                }
                // Konbini: HEAP creates the order in processPayment; skip Adyen voucher UI and redirect.
                case "voucher": {
                    if (paymentMethod.type === EcontextPaymentMethod.Stores) {
                        const orderId = result.orderDraftId || result.orderNumber;
                        if (!orderId) {
                            throw new Error(
                                "Konbini voucher response missing orderDraftId and orderNumber"
                            );
                        }
                        logInfo("onSubmit: Konbini voucher — one-step redirect to confirmation");
                        actions.resolve?.(result as CheckoutAdvancedFlowResponse);
                        await handleSuccessfulPayment(
                            result.orderNumber ?? orderId,
                            result.orderDraftId,
                            paymentMethod.brand ?? paymentMethod.type
                        );
                        return;
                    }
                    logError("Received unsupported voucher action from Adyen", {
                        resultAction,
                        paymentMethod: paymentMethod.type,
                    });
                    throw new Error(`Action type: voucher not supported for ${paymentMethod.type}`);
                }
                case "bankTransfer": {
                    logError("Received unsupported action type from Adyen", {
                        resultAction,
                    });
                    throw new Error(`Action type: bankTransfer not supported by HEAP payment yet`);
                }

                // BLIK: user approves in their banking app while Adyen polls for status.
                // Order is created in processPayment (freezeCart), so confirmPayment is not used.
                // Store the orderNumber to complete the flow in onAdditionalDetails.
                case "await": {
                    releaseLockscreen();

                    if (!result.orderNumber) {
                        throw new Error("Await Payment requires orderNumber but none was provided");
                    }

                    awaitPaymentState = {
                        active: true,
                        orderNumber: result.orderNumber,
                        orderDraftId: result.orderDraftId,
                        paymentMethod: paymentMethod.brand ?? paymentMethod.type,
                    };
                    dispatchAction(
                        component,
                        resultAction.action,
                        resultCode,
                        actions,
                        "onSubmit/await"
                    );

                    return;
                }
            }
            // This will cause a TS error if another actionType is added in the future.
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = actionType;
        } catch (error) {
            handleFailedPayment({
                message: "Error during onSubmit",
                component,
                actions,
                payload: {
                    error,
                    state,
                },
            });
        }
    };

    const onSubmit = ((state, component, actions) =>
        handleSubmit(state, component, actions)) as CoreConfiguration["onSubmit"];

    const handleAdditionalDetails = async (
        state: Parameters<NonNullable<CoreConfiguration["onAdditionalDetails"]>>[0],
        component: Parameters<NonNullable<CoreConfiguration["onAdditionalDetails"]>>[1],
        actions: Parameters<NonNullable<CoreConfiguration["onAdditionalDetails"]>>[2]
    ) => {
        logInfo("onAdditionalDetails start", {
            stateData: state.data,
        });
        try {
            onPaymentStart();

            // HEAP freezes the cart when it places a UPI order and rejects a confirm for it, so the
            // QR's outcome is HEAP's to record from Adyen; the confirmation page shows it.
            if (openPendingUpiOrder("QR completed")) return;

            // Await-type payments (BLIK): HEAP pre-creates the order in processPayment.
            // Skip confirmPayment and complete using the returned orderNumber.
            if (awaitPaymentState.active) {
                const { orderNumber, orderDraftId, paymentMethod } = awaitPaymentState;
                awaitPaymentState = { active: false };

                // The additional-details payload for await payment flows doesn't carry a resultCode.
                // Parse it defensively, any non-authorised result means the bank refused.
                let awaitResultCode: ResultCode | undefined;
                try {
                    const awaitResult = parsePaymentResult(JSON.stringify(state.data));
                    awaitResultCode = awaitResult?.resultCode as ResultCode | undefined;
                } catch {
                    // ignore
                }

                if (awaitResultCode && isFailedAdyenResult(awaitResultCode)) {
                    logInfo("onAdditionalDetails: Await Payment (BLIK) refused by bank", {
                        orderNumber,
                        awaitResultCode,
                    });
                    handleFailedPayment({
                        message: "BLIK payment refused by bank during await",
                        component,
                        actions,
                        payload: { awaitResultCode, orderNumber },
                    });
                    return;
                }

                logInfo("onAdditionalDetails: Await Payment (BLIK) confirmed", { orderNumber });
                actions?.resolve?.({
                    resultCode: "Authorised",
                } as CheckoutAdvancedFlowResponse);
                handleSuccessfulPayment(orderNumber, orderDraftId, paymentMethod);

                return;
            }

            const currentCart = await getCurrentCart?.();
            if (getCurrentCart && (!currentCart || currentCart.id !== cartId)) {
                logError("Current CommerceTools cart is unavailable or changed", {
                    currentCartId: currentCart?.id,
                    expectedCartId: cartId,
                });
                onError(HEAP_PAYMENT_ERRORS.CartValidationFailure);
                return;
            }

            const basketValidation = await validateBasket?.(undefined, currentCart, true);
            if (basketValidation?.isValid === false) {
                logError("Basket validation failed during onAdditionalDetails", {
                    reason: basketValidation.reason,
                    cartId: basketValidation.cartId,
                    skus: basketValidation.skus,
                    errorCode: basketValidation.errorCode,
                });
                handleFailedPayment({
                    message: "Basket validation failed during onAdditionalDetails",
                    component,
                    actions,
                    payload: {
                        basketValidation,
                        state,
                    },
                });
                return;
            }

            // Another instance already placed this order, so there is no pending confirm left to
            // join — without this the remounted instance would start a fresh one for a dead draft.
            if (isPaymentAlreadyCompleted()) {
                logInfo("Payment already completed, skipping duplicate confirm");
                return;
            }
            // No id to confirm against: the order-draft map is keyed by flow, so calling anyway
            // guarantees a miss and surfaces as an opaque server error. Fail fast and name it.
            const resolvedPaymentFlowId = resolvePaymentFlowId();
            if (!resolvedPaymentFlowId) {
                handleFailedPayment({
                    message: "Cannot confirm payment: no payment flow id available",
                    component,
                    actions,
                    // Keys only: the values are one-time Adyen tokens, while the shape is what
                    // names the method (payload = qrCode/Swish, redirectResult, threeDSResult).
                    payload: {
                        detailKeys: Object.keys(
                            (state.data as { details?: Record<string, unknown> })?.details ?? {}
                        ),
                        hasRedirectPaymentFlowId: Boolean(redirectPaymentFlowId),
                    },
                });
                return;
            }
            // Reuse a pending confirm so a duplicate poller shares its result instead of sending a
            // second call, which would find the draft entry already consumed and throw. Cleared on
            // settle so chained actions and post-failure retries can confirm again.
            const confirmKey = `${originalCartId}:${resolvedPaymentFlowId}`;
            const request =
                confirmsInFlight.get(confirmKey) ??
                confirmPayment({
                    cartId,
                    providerType: "Adyen",
                    paymentRequestJson: JSON.stringify(state.data),
                    redirectResult,
                    paymentFlowId: resolvedPaymentFlowId,
                });
            confirmsInFlight.set(confirmKey, request);

            let result: Awaited<typeof request>;
            try {
                result = await request;
            } finally {
                if (confirmsInFlight.get(confirmKey) === request) {
                    confirmsInFlight.delete(confirmKey);
                }
            }

            const resultCode = result.resultCode as ResultCode;
            // Order was successful
            if (result?.orderNumber && (result?.isPaymentComplete || result?.isPaymentPending)) {
                // Redirect flow fully resolved; this is a fresh instance (returning from an
                // off-site redirect), so state.data doesn't carry the payment method — read
                // the marker set before leaving (see onSubmit's "redirect" case) instead.
                // handleSuccessfulPayment clears the marker once it's read here.
                const paymentMethod =
                    sessionStorage.getItem("adyen_payment_method_type") ?? undefined;
                actions?.resolve?.(result as CheckoutAdvancedFlowResponse);
                handleSuccessfulPayment(
                    result.orderNumber,
                    result.orderDraftId,
                    paymentMethod,
                    result.wasAlreadyConfirmed
                );
                return;
            }

            // Adyen's "Cancelled" resultCode alone doesn't tell a shopper backing out of a
            // redirect/3DS/off-site flow apart from a provider- or merchant-side cancellation, so
            // resultCode can't gate this. The marker below is a shopper-specific signal instead:
            // it's only set right before we ourselves sent this attempt off-site (onSubmit's
            // "redirect"/"threeDS2"/"sdk" cases), so its presence here proves this onAdditionalDetails
            // call is the direct continuation of that shopper interaction. Without it, a "Cancelled"
            // can't be attributed to anything the shopper did in this attempt, so it falls through
            // to isFailedAdyenResult below and is treated as a failure instead of silently swallowed.
            if (resultCode === "Cancelled" && sessionStorage.getItem("adyen_payment_method_type")) {
                logInfo("Payment cancelled by shopper during onAdditionalDetails", { resultCode });
                sessionStorage.removeItem("adyen_payment_method_type");
                releaseLockscreen();
                actions?.resolve?.({ resultCode: "Cancelled" } as CheckoutAdvancedFlowResponse);
                component?.setStatus("ready");
                onCancel?.();
                return;
            }

            // Check if we need to handle another action. Gated on the action itself, not on the
            // parsed wrapper: any responseJson object is truthy, so a payload without an action
            // used to reach the deref below and throw a TypeError the shopper saw as a failure.
            const followUpAction = parsePaymentAction(result.responseJson)?.action;
            if (followUpAction?.type) {
                logInfo("Received additional action in onAdditionalDetails", {
                    actionType: followUpAction.type,
                });

                // Keep the "adyen_payment_method_type" marker: a chained action
                // (e.g. another redirect or 3DS challenge) may send the user off-site again,
                // and the bfcache recovery listener needs the marker to detect the return.
                // component/actions may be undefined when onAdditionalDetails is triggered
                // via checkout.submitDetails() (e.g. handleRedirectResult) rather than by
                // a mounted dropin component.
                dispatchAction(
                    component,
                    followUpAction,
                    resultCode,
                    actions,
                    "onAdditionalDetails"
                );
                return;
            }

            // Still waiting on the shopper — a qrCode poll can report pending before there is an
            // order. Release the UI and let the SDK keep polling instead of failing the payment.
            // Deliberately not resolved: a non-final result would tell the SDK the flow is done.
            if (result?.isPaymentPending && !result?.orderNumber) {
                logInfo("Confirm still pending, awaiting a further poll", { resultCode });
                releaseLockscreen();
                return;
            }

            const adyenResult = parsePaymentResult(result.responseJson);
            if (isFailedAdyenResult(adyenResult.resultCode)) {
                // Terminal failure — no further redirect expected, clear the marker.
                sessionStorage.removeItem("adyen_payment_method_type");
                handleFailedPayment({
                    message: `onAdditionalDetails: ${
                        (adyenResult as AdyenRefusalResponse).refusalReason ??
                        "Payment failed with Adyen result"
                    }`,
                    component,
                    actions,
                    payload: {
                        adyenResult,
                        result,
                        onAdditionalDetailsData: state.data,
                    },
                    severity: "warn",
                });
                return;
            }

            // If we reach this point we had a payment scenario we didn't know how to handle.
            throw new Error(`Reached end of onAdditionalDetails`, {
                cause: {
                    result,
                    resultCode,
                },
            });
        } catch (error) {
            handleFailedPayment({
                message: "Error during onAdditionalDetails",
                component,
                actions,
                payload: {
                    error,
                    state,
                },
            });
        }
    };

    const onAdditionalDetails = ((state, component, actions) =>
        handleAdditionalDetails(
            state,
            component,
            actions
        )) as CoreConfiguration["onAdditionalDetails"];

    const onPaymentFailed: CoreConfiguration["onPaymentFailed"] = (error, _component) => {
        logWarn("onPaymentFailed event triggered", {
            error,
        });
    };

    const coreConfiguration: CoreConfiguration = {
        clientKey: apiKey,
        /**
         * https://app.datadoghq.eu/error-tracking?query=&fromUser=true&issue_states=open&order=total_count&refresh_mode=sliding&source=all&sp=%5B%7B%22p%22%3A%7B%22issueId%22%3A%22b767044a-d4df-11f0-8b8f-da7ad0900005%22%7D%2C%22i%22%3A%22error-tracking-issue%22%7D%5D&from_ts=1782730741442&to_ts=1783335541442&live=true
         * https://github.com/Adyen/adyen-web/issues/2778
         *
         * All english locales needs to be mapped to en-US as Adyen doesn't support regional variations
         * https://github.com/Adyen/adyen-web/blob/main/packages/server/api/translations.js
         * https://github.com/Adyen/adyen-web/tree/main/packages/server/translations
         *
         */
        locale: locale.includes("en-") ? "en-US" : locale,
        amount,
        environment:
            process.env.NEXT_PUBLIC_ADYEN_ENVIRONMENT.toLowerCase() as CoreConfiguration["environment"],
        countryCode: activeSiteCountry,
        translations: parsedTranslation,
        showPayButton: true,
        analytics: {
            enabled: true,
        },
        paymentMethodsResponse: { paymentMethods: toAdyenPaymentMethods(paymentMethods) },
        onActionHandled: (actionHandled) => {
            logInfo("onActionHandled event", {
                componentType: actionHandled.componentType,
                actionDescription: actionHandled.actionDescription || "UNKNOWN",
                originalAction: actionHandled.originalAction,
            });
        },
        beforeRedirect: (resolve, _reject, _redirectData) => {
            resolve();
        },
        onSubmit,
        onAdditionalDetails,
        onPaymentFailed,
        onError(error, _component) {
            logError("onError event triggered in Adyen Checkout", {
                error,
            });
            releaseLockscreen();
            openPendingUpiOrder("QR ended");
        },
    };

    const initializeCheckout = () => {
        return AdyenCheckout(coreConfiguration);
    };
    const checkoutInstance = await initializeCheckout();

    const removeRedirectResult = () => {
        // Remove the redirect result (and its paired flow id) from the URL since they've been used.
        if (window.history.replaceState) {
            const url = new URL(window.location.href);
            url.searchParams.delete("redirectResult");
            url.searchParams.delete("paymentFlowId");
            window.history.replaceState({}, document.title, url.toString());
        }
    };

    const handleRedirectResult = () => {
        if (redirectResult) {
            // Consume the redirectResult exactly once: strip it from the URL up-front so a failed
            // confirm or a remount can't replay this token against a different attempt's order draft.
            removeRedirectResult();
            const handleSubmitDetailsFailure = (error: unknown) => {
                logError("Failed to submit redirect result", { error });
                onError(HEAP_PAYMENT_ERRORS.PAYMENT_FAILED);
                releaseLockscreen();
            };
            try {
                void Promise.resolve(
                    checkoutInstance.submitDetails({ details: { redirectResult } })
                ).catch(handleSubmitDetailsFailure);
            } catch (error) {
                handleSubmitDetailsFailure(error);
            }
        }
    };

    // We handle the redirect result before anything else to make sure Adyen is in the correct state fromt he start.
    handleRedirectResult();

    // Rebuilds the QR from the stored action after a reload, so the SDK resumes polling this
    // attempt. Returning the shopper to the pay button instead lets them start a second payment
    // while the first may already be authorised. Scoped to this cart, and skipped once the order
    // is placed so a completed flow can't be replayed into a live QR.
    const mountPendingQrAction = (): boolean => {
        const pending = readPendingQrAction();
        if (!pending || pending.cartId !== originalCartId || isPaymentAlreadyCompleted()) {
            return false;
        }
        try {
            // paymentMethodComponents is a dropin option, and the dropin is what registers those
            // components on the core. Restoring happens before any dropin exists, so register them
            // here or createFromAction cannot resolve the action element and throws.
            checkoutInstance.register(...(baseConfig.paymentMethodComponents ?? []));
            pendingQrElement = checkoutInstance.createFromAction(pending.action);
            pendingQrElement.mount(targetElement);
            pendingUpiOrder = pending.placedOrder;
            onPendingQrChange?.(true);
            logInfo("Restored pending qrCode action after reload", {
                actionType: pending.action.type,
                paymentFlowId: pending.paymentFlowId,
            });
            return true;
        } catch (error) {
            // Fall back to the dropin: a stale or unsupported action must not block checkout.
            logWarn("Could not restore pending qrCode action", { error });
            sessionStorage.removeItem(PENDING_QR_ACTION_STORAGE_KEY);
            return false;
        }
    };

    const mountDropin = () => {
        try {
            if (!targetElement) {
                throw new Error("Target element for Adyen Dropin is not defined");
            }
            if (mountPendingQrAction()) {
                return;
            }
            const dropin = new Dropin(checkoutInstance, dropinConfiguration);
            dropinElement = dropin;
            dropin.mount(targetElement);
            logInfo("Adyen Dropin mounted successfully");
        } catch (error) {
            logError("Error during mounting Adyen Dropin", {
                error,
            });
        }
    };

    // Abandons a displayed QR and restores the payment methods. The shopper is otherwise stuck on
    // the QR with no way to pick another method. Client-side only by design: it cannot cancel the
    // request at Adyen, so a shopper who has already paid in the app still completes that payment.
    const cancelPendingQr = () => {
        logInfo("Pending qrCode cancelled by the shopper");
        sessionStorage.removeItem(PENDING_QR_ACTION_STORAGE_KEY);
        sessionStorage.removeItem("adyen_payment_method_type");
        pendingUpiOrder = undefined;
        onPendingQrChange?.(false);
        // Tear down whatever is showing the QR before remounting. Mounting a dropin over a live one
        // leaves the QR on screen, which is why closing it has to unmount first.
        try {
            pendingQrElement?.unmount();
            dropinElement?.componentFromAction?.unmount();
            dropinElement?.unmount();
        } catch (error) {
            logWarn("Could not tear down the pending qrCode", { error });
        }
        pendingQrElement = undefined;
        dropinElement = undefined;
        // The stored action is gone, so this mounts a fresh dropin with the methods selectable.
        mountDropin();
    };

    const updateAmount = (value: PaymentAmount) => {
        logInfo("Updating payment amount", {
            value,
        });
        checkoutInstance.update({
            amount: value,
        });
    };

    const updateCartId = (newCartId: string) => {
        logInfo("Updating cartId", {
            previousCartID: cartId,
            originalCartId,
            newCartId,
        });
        cartId = newCartId;
    };

    const updateValidateBasket = (newValidateBasket: typeof originalValidateBasket) => {
        logInfo("Updating validateBasket function");
        validateBasket = newValidateBasket;
    };

    return {
        updateAmount,
        mountDropin,
        updateCartId,
        updateValidateBasket,
        cancelPendingQr,
    };
};
